import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  clientApiService,
  FinancialSummary,
  InvoiceListItem,
  ProjectItem,
} from "../../services/client-api.service";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
  partial: { label: "Pago parcial", className: "bg-blue-100 text-blue-700" },
  paid: { label: "Pagada", className: "bg-green-100 text-green-700" },
  overdue: { label: "Vencida", className: "bg-red-100 text-red-700" },
  sent: { label: "Enviada", className: "bg-slate-100 text-slate-700" },
  cancelled: { label: "Cancelada", className: "bg-slate-100 text-slate-400" },
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(n);
}

function formatDate(t: number | null) {
  if (!t) return "—";
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function PaymentsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectIdParam = searchParams.get("projectId") || "";

  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [summaryData, invoicesData, projectsData] = await Promise.all([
        clientApiService.getFinancialSummary(projectIdParam || undefined),
        clientApiService.listInvoices({
          projectId: projectIdParam || undefined,
          status: statusFilter,
        }),
        clientApiService.listProjects(),
      ]);
      setSummary(summaryData);
      setInvoices(invoicesData);
      setProjects(projectsData);
    } catch (err: any) {
      setError(err?.message || "Error al cargar estado de cuenta");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdParam, statusFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices;
    const term = search.trim().toLowerCase();
    return invoices.filter(
      (i) =>
        i.number.toLowerCase().includes(term) ||
        (i.projectName || "").toLowerCase().includes(term)
    );
  }, [invoices, search]);

  const handleProjectChange = (id: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (id) newParams.set("projectId", id);
    else newParams.delete("projectId");
    setSearchParams(newParams);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Estado de Cuenta</h2>
          <p className="text-sm text-slate-500 mt-1">
            Resumen de tus facturas y pagos
          </p>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          className="text-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
        >
          {isLoading ? "Actualizando..." : "🔄 Actualizar"}
        </button>
      </div>

      {/* Resumen */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            icon="🧾"
            label="Total facturado"
            value={formatCurrency(summary.totalInvoiced)}
          />
          <SummaryCard
            icon="✅"
            label="Total pagado"
            value={formatCurrency(summary.totalPaid)}
          />
          <SummaryCard
            icon="⏳"
            label="Saldo pendiente"
            value={formatCurrency(summary.pendingAmount)}
            highlight={summary.pendingAmount > 0}
          />
          <SummaryCard
            icon="⚠️"
            label="Vencido"
            value={formatCurrency(summary.overdueAmount)}
            highlight={summary.overdueAmount > 0}
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Buscar por número de factura o proyecto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none text-sm"
        />
        <select
          value={projectIdParam}
          onChange={(e) => handleProjectChange(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="">Todos los proyectos</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="all">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="partial">Pago parcial</option>
          <option value="paid">Pagada</option>
          <option value="overdue">Vencida</option>
        </select>
      </div>

      {/* Contenido */}
      {isLoading && invoices.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error}
          <button onClick={load} className="ml-3 underline font-medium">
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="text-5xl mb-3 opacity-40">🧾</div>
          <h3 className="text-lg font-semibold text-slate-900">
            {invoices.length === 0 ? "Sin facturas aún" : "Sin resultados"}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {invoices.length === 0
              ? "Cuando el despacho emita facturas, aparecerán aquí."
              : "Prueba con otros filtros."}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="py-3 px-4">Factura</th>
                  <th className="py-3 px-4">Proyecto</th>
                  <th className="py-3 px-4">Emisión</th>
                  <th className="py-3 px-4">Vence</th>
                  <th className="py-3 px-4 text-right">Total</th>
                  <th className="py-3 px-4 text-right">Pagado</th>
                  <th className="py-3 px-4 text-center">Estado</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((inv) => {
                  const status = STATUS_LABELS[inv.status] || {
                    label: inv.status,
                    className: "bg-slate-100 text-slate-700",
                  };
                  return (
                    <tr key={inv.id} className="hover:bg-slate-50 transition">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{inv.number}</div>
                        {inv.pendingReceipts > 0 && (
                          <span className="text-xs text-yellow-600">
                            {inv.pendingReceipts} comprobante(s) pendiente(s)
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600 truncate max-w-[160px]">
                        {inv.projectName || "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-500">{formatDate(inv.dateIssued)}</td>
                      <td className="py-3 px-4 text-slate-500">{formatDate(inv.dateDue)}</td>
                      <td className="py-3 px-4 text-right font-medium text-slate-900">
                        {formatCurrency(inv.total)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-green-700 font-medium">
                          {formatCurrency(inv.paidAmount)}
                        </span>
                        {inv.pendingAmount > 0 && (
                          <div className="text-xs text-slate-400">
                            Falta: {formatCurrency(inv.pendingAmount)}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/dashboard/payments/${inv.id}`}
                          className="text-primary hover:underline font-medium text-xs"
                        >
                          Ver →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: string;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`bg-white rounded-2xl border p-4 ${
        highlight ? "border-primary/30" : "border-slate-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-xs text-slate-500 font-medium">{label}</span>
      </div>
      <p className={`text-xl font-bold ${highlight ? "text-primary" : "text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
}